import { useEffect, useRef } from "react";
import { Toast } from 'primereact/toast';

const map_key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const DEBOUNCE_MS = 400;
const MAX_CACHED_LEGS = 1000;
// Una alternativa más corta solo gana si tarda como máximo esto más que la más rápida
const TIME_TOLERANCE_S = 60;

// Routes API de Google (sustituye a la Directions API de Mapbox)
const BASE_URL = 'https://routes.googleapis.com/directions/v2:computeRoutes';
const FIELD_MASK = 'routes.distanceMeters,routes.duration,routes.polyline';
const pointKey = (p) => `${p.lng},${p.lat}`;
const legKey = (a, b) => `${pointKey(a)};${pointKey(b)}`;
const toLatLng = ([lng, lat]) => ({ lat, lng });
const seconds = (route) => parseFloat(route.duration) || 0; // viene como "123s"
const toWaypoint = (point) => ({
    location: { latLng: { latitude: point.lat, longitude: point.lng } },
});

/**
 * Un tramo (parada a parada). Se toma la ruta más rápida (routes[0], la misma que muestra Google Maps)
 * y solo se cambia a una alternativa más corta si tarda casi lo mismo (TIME_TOLERANCE_S). Así no
 * brinca a un camino con muchos más km cuando el tiempo es parecido, pero tampoco prefiere atajos
 * lentos (p. ej. por dentro de un pueblo en vez de la carretera). Las alternativas solo existen en
 * peticiones de dos puntos.
 */
const fetchBestLeg = async (from, to) => {
    const response = await fetch(BASE_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': map_key,
            'X-Goog-FieldMask': FIELD_MASK,
        },
        body: JSON.stringify({
            origin: toWaypoint(from),
            destination: toWaypoint(to),
            travelMode: 'DRIVE',
            routingPreference: 'TRAFFIC_UNAWARE',
            computeAlternativeRoutes: true,
            polylineQuality: 'HIGH_QUALITY', // por defecto es OVERVIEW (simplificado) y no sigue la calle
            polylineEncoding: 'GEO_JSON_LINESTRING',
        }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error?.message ?? `Routes API respondió ${response.status}`);
    if (!data.routes?.length) throw new Error('No se encontró ruta para un tramo');

    const fastest = data.routes[0];
    const best = data.routes
        .filter(route => seconds(route) - seconds(fastest) <= TIME_TOLERANCE_S)
        .reduce((a, b) => (b.distanceMeters < a.distanceMeters ? b : a));

    return {
        coords: best.polyline.geoJsonLinestring.coordinates.map(toLatLng),
        distance: best.distanceMeters ?? 0,
        duration: seconds(best),
    };
};

/**
 * Calcula la ruta (línea + distancia/tiempo) directamente a partir de las
 * paradas: las paradas SON los waypoints, no hay un concepto separado.
 *
 * Cada tramo (parada i -> parada i + 1) se guarda por sus coordenadas: la primera vez se piden
 * todos y después solo los que cambian (unas 2 peticiones por movimiento o alta de parada).
 *
 * Este componente no dibuja markers (eso ya lo hace DraggableMarker) ni la
 * línea (eso lo dibuja el componente padre con <Polyline>) — solo calcula.
 */
const RoutingMachine = ({ stops, onRouteFound }) => {
    const toast = useRef(null);
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);
    const legsCache = useRef(new Map());
    const onRouteFoundRef = useRef(onRouteFound);

    useEffect(() => { onRouteFoundRef.current = onRouteFound; }, [onRouteFound]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!stops || stops.length < 2) {
            onRouteFoundRef.current?.([], { distance: 0, duration: 0 });
            return;
        }

        debounceRef.current = setTimeout(async () => {
            const requestId = ++requestIdRef.current;
            const cache = legsCache.current;

            try {
                if (cache.size > MAX_CACHED_LEGS) cache.clear(); // antes de ver qué falta
                const points = stops.map(s => ({ lat: Number(s.lat), lng: Number(s.lng) }));
                const keys = points.slice(1).map((to, i) => legKey(points[i], to));
                const missing = keys.map((key, i) => i).filter(i => !cache.has(keys[i]));

                if (missing.length) {
                    const legs = await Promise.all(missing.map(i => fetchBestLeg(points[i], points[i + 1])));
                    legs.forEach((leg, j) => cache.set(keys[missing[j]], leg));
                }

                if (requestId !== requestIdRef.current) return; // respuesta obsoleta, ignorar

                let coordinates = [];
                let totalDistance = 0;
                let totalDuration = 0;

                keys.forEach((key, i) => {
                    const leg = cache.get(key);
                    // El primer punto de cada tramo es el último del anterior (la misma parada)
                    coordinates = coordinates.concat(i === 0 ? leg.coords : leg.coords.slice(1));
                    totalDistance += leg.distance;
                    totalDuration += leg.duration;
                });

                onRouteFoundRef.current?.(coordinates, { distance: totalDistance, duration: totalDuration });
            } catch (error) {
                console.error('Error calculando ruta:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error de ruta',
                    detail: 'No se pudo calcular la ruta entre las paradas.'
                });
            }
        }, DEBOUNCE_MS);

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [stops]);

    return <Toast ref={toast} position="bottom-right" />;
};

export default RoutingMachine;
